import { getEnglishI18n } from "@/modules/l10n/english.ts";
import { getReactNodeText, isElementType } from "@/utils/getReactNodeText.ts";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { Trans, type TransProps } from "@lingui/react";
import { hasOwn } from "@warrenio/utils/collections/getOwnProperty";
import { isObject } from "@warrenio/utils/isObject";
import { isValidElement, type ReactElement } from "react";
import { mapValues } from "remeda";

/**
 * Translatable text.
 * Can either be a raw string (non-translated) or text inside a <Trans> element.
 *
 * @see {@link getEnglishText} and {@link getTransText} to get the translated `string` (when used outside of React).
 */
export type Translatable = string | ReactElement<TransProps>;
export type Localizable = Translatable | MessageDescriptor;

export function getEnglishText(text: Localizable): string {
    return getTransText(text, getEnglishI18n());
}

export function isMessageDescriptor(obj: unknown): obj is MessageDescriptor {
    return isObject(obj) && hasOwn(obj, "id") && typeof obj.id === "string";
}

export function getTransText(text: Localizable, i18n: I18n): string {
    if (isMessageDescriptor(text)) {
        return i18n.t(text);
    }
    return getReactNodeText(text, (el) => {
        if (!isElementType(el, Trans)) {
            return;
        }

        const { id, values, components, message } = el.props;

        if (import.meta.env.DEV) {
            if (components != null) {
                throw new Error(`Can not use sub-components inside <Trans> being rendered as text: ${message} [${id}]`);
            }
        }

        return i18n._(
            id,
            values != null
                ? mapValues(values, (v) => (isValidElement(v) && isElementType(v, Trans) ? getTransText(v, i18n) : v))
                : undefined,
        );
    });
}

export function getTransTextWithDefault(i18n: I18n, text: Localizable | null | undefined, fallback: string): string {
    if (text == null) {
        return fallback;
    }

    return getTransText(text, i18n);
}

//#region Error utilities
export interface LocalizedError {
    localeMessage: Localizable;
}

export class SimpleLocalizedError extends Error implements LocalizedError {
    constructor(
        public localeMessage: Localizable,
        options?: ErrorOptions,
    ) {
        super(getEnglishText(localeMessage), options);
    }
}
//#endregion
